import {
    useClickOutside,
} from "@mrflark/core/hooks";
import {
    Check,
    ChevronDown,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    useCallback,
    useRef,
    useState,
} from "preact/hooks";

export type MultiSelectOption = {
    value: string;
    label: string;
};

type MultiSelectDropdownProps = {
    options: MultiSelectOption[];
    selected: string[];
    onChange: (selected: string[]) => void;
    placeholder?: string;
    className?: string;
};

export const MultiSelectDropdown = ({
    options,
    selected,
    onChange,
    placeholder = "All",
    className = "",
}: MultiSelectDropdownProps): ComponentChildren => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useClickOutside([containerRef], useCallback(() => setOpen(false), []));

    const toggleOption = (value: string): void => {
        if (selected.includes(value)) {
            onChange(selected.filter((item) => item !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const triggerLabel = selected.length === 0
        ? placeholder
        : selected.length === 1
        ? options.find((opt) => opt.value === selected[0])?.label ?? selected[0]
        : `${selected.length} of ${options.length}`;

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`
                    flex items-center gap-1 w-full px-2 py-1.5 rounded-lg
                    bg-bg-tertiary border border-border
                    text-sm text-left cursor-pointer
                    focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
                    ${
                    selected.length > 0
                        ? "text-text-primary"
                        : "text-text-secondary"
                }
                `.trim().replace(/\s+/g, " ")}
            >
                <span className="truncate flex-1">{triggerLabel}</span>
                <ChevronDown
                    size={14}
                    className={`shrink-0 text-text-muted transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full min-w-[10rem] bg-bg-secondary border border-border rounded-lg shadow-lg py-1">
                    {options.map((option) => {
                        const isSelected = selected.includes(option.value);
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => toggleOption(option.value)}
                                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left hover:bg-bg-tertiary transition-colors"
                            >
                                <div
                                    className={`
                                    w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors
                                    ${
                                        isSelected
                                            ? "bg-accent border-accent"
                                            : "border-border bg-bg-secondary"
                                    }
                                `.trim().replace(/\s+/g, " ")}
                                >
                                    {isSelected && (
                                        <Check
                                            size={12}
                                            strokeWidth={3}
                                            className="text-white"
                                        />
                                    )}
                                </div>
                                <span
                                    className={isSelected
                                        ? "text-text-primary"
                                        : "text-text-secondary"}
                                >
                                    {option.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
